/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import useRouter from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';
import { CardV2 } from '@coursera/coursera-ui';

import { AUTHENTICATION_MODAL_MODES, HEADER_HEIGHT_PX } from 'bundles/authentication/constants';
import AuthenticationContent from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import Header from 'bundles/authentication/shared/components/authentication-modal/Header';
import { toValidRedirect } from 'bundles/authentication/utils/navigationUtils';
import GlobalFooter from 'bundles/page-footer/components/global-footer/GlobalFooter';
import LoggedOutPageHeader from 'bundles/page-header/components/optimized/LoggedOutPageHeader';
import { CourseraMetatags } from 'bundles/seo';

import _t from 'i18n!nls/authentication';

const styles = {
  cardContainer: () => css`
    background-color: var(--cds-color-blue-50);
    margin: auto;
    width: 100%;
    padding: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding: 0;
      overflow-y: hidden;
    }

    .standalone-auth-card {
      max-width: 518px;
      background-color: var(--cds-color-white-0);
      margin: auto;

      ${breakpoints.up('sm')} {
        padding: var(--cds-spacing-300) 0;
      }

      ${breakpoints.down('sm')} {
        width: 80%;
      }

      ${breakpoints.down('xs')} {
        width: 100%;
        max-width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        padding-top: ${HEADER_HEIGHT_PX}px;
      }
    }
  `,
  hideOnMobile: () => css`
    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
  mobileOnlyHeader: () => css`
    ${breakpoints.up('sm')} {
      display: none;
    }
  `,
};

function StandaloneAuthPage() {
  const router = useRouter();
  const { pathname } = router.location;
  const isLogin = pathname.includes('/login');

  const redirectTo = toValidRedirect(router.location.query.redirectTo || router.location.query.r);
  const context = {
    action: router.location.query.action,
    accountType: router.location.query.serviceCode,
    mode: router.location.query.completeMode,
    service: router.location.query.authType,
  };

  const metaTitle = isLogin ? _t('Coursera Login - Continue Learning') : _t('Coursera Signup - Start Learning');
  const metaDescription = isLogin
    ? _t(
        'Log into your Coursera account with your email address, Google, Facebook, or Apple credential. Learn online and earn valuable credentials from top universities like Yale, Michigan, Stanford, and leading companies like Google and IBM.'
      )
    : _t(
        'Join Coursera for free. Create a profile to get a personalized learning experience with course recommendations.'
      );

  const seoMetadata = {
    title: metaTitle,
    ogAndTwitterTitle: metaTitle,
    description: metaDescription,
    disableCourseraDescriptionSuffix: true,
  };

  return (
    <div className={isLogin ? 'rc-LoginPage' : 'rc-SignupPage'} key={isLogin ? 'login-page' : 'signup-page'}>
      <section css={styles.hideOnMobile}>
        <LoggedOutPageHeader hideAuth isStandaloneAuth />
      </section>
      <div css={styles.cardContainer} data-e2e="auth-modal">
        <CardV2 rootClassName="standalone-auth-card" isInteractive={false}>
          <Header css={styles.mobileOnlyHeader} logoAsLink linkTrackingName="auth_header_logo" />
          <AuthenticationContent
            isStandaloneAuth
            context={context}
            defaultMode={isLogin ? AUTHENTICATION_MODAL_MODES.LOGIN : AUTHENTICATION_MODAL_MODES.SIGNUP}
            redirectTo={redirectTo}
          />
        </CardV2>
      </div>
      <section css={styles.hideOnMobile}>
        <GlobalFooter />
      </section>
      <CourseraMetatags metaNameAndProperties={seoMetadata} />
    </div>
  );
}

export default StandaloneAuthPage;
